// Merxo-App: Nach einem Kaltstart weiter, wo die Person war (Befund der Geräteprüfung 16.10.).
//
// iOS und Android beenden Apps im Hintergrund – etwa während die Kassenkraft zur Kartenterminal-App wechselt. Die App
// lädt beim Start immer MERXO_SERVER/; ohne diese Erinnerung stünde die Kasse danach auf der Startseite und müsste über
// „Kasse öffnen“ › „Kassieren“ zurück (der offene Bon liegt ohnehin auf dem Server). Gemerkt wird je Gerät der letzte
// Pfad mit Person und Betrieb. Wiederhergestellt wird nur
//   • in der App (im Browser gibt es Tabs und Adressen),
//   • beim ersten Aufbau nach dem Start der App (nicht bei jedem Besuch der Startseite, nicht nach „Neu laden“),
//   • wenn die App auf / startet, für dieselbe Person im selben Betrieb und höchstens 12 Stunden später
//     (so lange gilt eine Sitzung ohne Tätigkeit).
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { nativeApp } from '../lib/native'
import { readStored, writeStored } from '../lib/storage'

const LAST_ROUTE = 'app.lastRoute'
const STARTED = 'merxo.app.started'
export const RESUME_MAX_AGE_MS = 12 * 60 * 60_000

/** Seiten, die nicht wiederhergestellt werden (vor der Anmeldung) */
const SKIP = /^\/(login|invitation|datenschutz)(\/|$)/
/** Zustände in der Adresse, die nur für den Augenblick gelten: Druckvorschau, „neu anlegen“ */
const TRANSIENT = ['print', 'new']

/** Pfad ohne flüchtige Zustände („/purchasing/orders/1?print=1“ → „/purchasing/orders/1“) */
export function lastingPath(path: string): string {
  const [pathname = '/', query = ''] = path.split('?', 2)
  if (!query) return pathname
  const params = new URLSearchParams(query)
  for (const key of TRANSIENT) params.delete(key)
  const rest = params.toString()
  return rest ? `${pathname}?${rest}` : pathname
}

export interface StoredRoute {
  path: string
  at: number
  userId: string
  tenantId: string
}

export function rememberRoute(full: string, who: { userId: string; tenantId: string }, now: number = Date.now()): void {
  const path = lastingPath(full)
  if (path === '/' || SKIP.test(path)) {
    writeStored(LAST_ROUTE, null)
    return
  }
  writeStored(LAST_ROUTE, { path, at: now, userId: who.userId, tenantId: who.tenantId } satisfies StoredRoute)
}

/** Ziel der Wiederherstellung – oder null. */
export function routeToResume(who: { userId: string; tenantId: string }, now: number = Date.now()): string | null {
  const stored = readStored<StoredRoute | null>(LAST_ROUTE, null)
  if (!stored || typeof stored.path !== 'string' || !stored.path.startsWith('/') || stored.path.startsWith('//')) return null
  if (stored.userId !== who.userId || stored.tenantId !== who.tenantId) return null
  if (!(now - stored.at >= 0 && now - stored.at <= RESUME_MAX_AGE_MS)) return null
  const path = lastingPath(stored.path)
  if (path === '/' || SKIP.test(path)) return null
  return path
}

/** Erster Aufbau nach dem Start der App? (sessionStorage überlebt Neu laden, aber nicht das Beenden der App) */
function firstStart(): boolean {
  try {
    if (window.sessionStorage.getItem(STARTED)) return false
    window.sessionStorage.setItem(STARTED, '1')
    return true
  } catch {
    return false
  }
}

/** Hinter der Sitzungsprüfung (angemeldet, Betrieb gewählt): merkt den Pfad und stellt ihn nach dem Kaltstart her. */
export function AppResume({ userId, tenantId }: { userId: string; tenantId: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const who = { userId, tenantId }
  const target = useRef<string | null | undefined>(undefined)
  if (target.current === undefined) {
    target.current = nativeApp() && location.pathname === '/' && firstStart() ? routeToResume(who) : null
  }

  useEffect(() => {
    if (target.current) void navigate(target.current, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- einmal nach dem Start
  }, [])

  const path = `${location.pathname}${location.search}`
  useEffect(() => {
    if (!nativeApp()) return
    // die Startseite vor der Weiterleitung überschreibt das Ziel nicht
    if (target.current && location.pathname === '/') return
    target.current = null
    rememberRoute(path, who)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- who ändert sich nur mit userId/tenantId
  }, [path, userId, tenantId])
  return null
}
